import moment from 'moment';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { fetchAgents } from '../../../redux/features/agentSlice';
import {
  updateApplicationWithReason,
  fetchLeads,
} from '../../../redux/features/adminLeadSlice';
import { RxCross2 } from 'react-icons/rx';
import { BsClockHistory } from 'react-icons/bs';
import { FaChevronDown } from 'react-icons/fa';
import MultiAgentAssignmentModal from '../../common/MultiAgentAssignmentModal';

const LeadsTable = ({ filteredApplications }) => {
  const { agents, isLoading: agentsLoading } = useSelector(
    (state) => state.agents,
  );
  const { isAssigning, assignError, isLoading } = useSelector(
    (state) => state.leads,
  );
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [filteredAgents, setFilteredAgents] = useState([]);

  // Multi-Agent Modal State
  const [multiAgentModal, setMultiAgentModal] = useState({
    show: false,
    applicationId: "",
    selectedAgents: [],
    currentAgents: [],
    reason: "",
  });
  const [isSubmittingAgents, setIsSubmittingAgents] = useState(false);

  const [statusModal, setStatusModal] = useState({
    show: false,
    applicationId: '',
    newStatus: '',
    reason: '',
    agentAssigned: true,
    visaStatus: '',
  });
  const [isSubmittingStatus, setIsSubmittingStatus] = useState(false);

  const DISPLAY_STATUS = [
    'Pending',
    'In-Progress',
    'Visa Applied',
    'Cancelled',
    'Withdrawn',
    // "Finalized",
  ];

  const toDisplayStatus = (apiStatus) => {
    if (apiStatus === 'VisaApplied') return 'Visa Applied';
    if (apiStatus === 'WithdrawnByClient' || apiStatus === 'WithdrawnByAgent')
      return 'Withdrawn';
    if (apiStatus === 'Submitted') return 'Pending';
    return apiStatus || '';
  };

  const toApiStatus = (displayStatus) => {
    if (displayStatus === 'Visa Applied') return 'VisaApplied';
    if (displayStatus === 'Withdrawn') return 'WithdrawnByAgent';
    if (displayStatus === 'Pending') return 'Submitted';
    return displayStatus;
  };

  const handleStatusChange = (e, application) => {
    const selected = e.target.value;
    const apiValue = toApiStatus(selected);

    const agentAssigned = !!application?.agent?._id;

    setStatusModal({
      show: true,
      applicationId: application._id,
      newStatus: apiValue,
      reason: '',
      agentAssigned,
      visaStatus: application?.visaStatus || '',
    });
  };

  const confirmStatusUpdate = async () => {
    if (!statusModal.reason || !statusModal.reason.trim()) return;
    try {
      setIsSubmittingStatus(true);
      await dispatch(
        updateApplicationWithReason({
          id: statusModal.applicationId,
          formData: {
            status: statusModal.newStatus,
            reason: statusModal.reason,
          },
        }),
      );
      setStatusModal({
        show: false,
        applicationId: '',
        newStatus: '',
        reason: '',
        agentAssigned: true,
        visaStatus: '',
      });
    } finally {
      setIsSubmittingStatus(false);
    }
  };

  // Agents moved to modal open trigger to avoid eager fetching on page load
  // useEffect(() => {
  //   if (agents.length === 0) {
  //     dispatch(fetchAgents());
  //   }
  // }, [agents.length, dispatch]);

  useEffect(() => {
    setFilteredAgents(agents.filter((agent) => agent.isActive));
  }, [agents]);

  const closeMultiAgentModal = () => {
    setMultiAgentModal({
      show: false,
      reason: '',
      applicationId: '',
      selectedAgents: [],
      currentAgents: [],
    });
  };

  const handleAgentButtonClick = (application) => {
    // Lazy load agents when modal opens
    if (agents.length === 0) {
      dispatch(fetchAgents());
    }

    const currentAgentIds = application.agents
      ? application.agents.map((agent) => agent._id)
      : application.agent
        ? [application.agent._id]
        : [];

    setMultiAgentModal({
      show: true,
      applicationId: application._id,
      selectedAgents: currentAgentIds,
      currentAgents: currentAgentIds,
    });
  };

  const handleAgentCheckboxChange = (agentId) => {
    setMultiAgentModal((prev) => {
      const newSelected = prev.selectedAgents.includes(agentId)
        ? prev.selectedAgents.filter((id) => id !== agentId)
        : [...prev.selectedAgents, agentId];
      return { ...prev, selectedAgents: newSelected };
    });
  };

  const handleMultiAgentSubmit = async () => {
    try {
      setIsSubmittingAgents(true);

      const agentsToAdd = multiAgentModal.selectedAgents.filter(
        (id) => !multiAgentModal.currentAgents.includes(id),
      );

      const agentsToRemove = multiAgentModal.currentAgents.filter(
        (id) => !multiAgentModal.selectedAgents.includes(id),
      );

      if (agentsToAdd.length > 0) {
        const response = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/api/application/assignApplication`,
          {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
              authorization: `Bearer ${localStorage.getItem('token')}`,
            },
            body: JSON.stringify({
              applicationId: multiAgentModal.applicationId,
              agentId: agentsToAdd,
              reason: multiAgentModal.reason,
            }),
          },
        );

        if (!response.ok) {
          throw new Error('Failed to assign agents');
        }
      }

      if (agentsToRemove.length > 0) {
        const response = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/api/application/removeAgent`,
          {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
              authorization: `Bearer ${localStorage.getItem('token')}`,
            },
            body: JSON.stringify({
              applicationId: multiAgentModal.applicationId,
              agentId: agentsToRemove,
              reason: multiAgentModal.reason,
            }),
          },
        );

        if (!response.ok) {
          throw new Error('Failed to remove agents');
        }
      }

      closeMultiAgentModal();
      dispatch(fetchLeads({}));
    } catch (error) {
      console.error('Error updating agents:', error);
    } finally {
      setIsSubmittingAgents(false);
    }
  };

  const getAgentDisplayText = (application) => {
    if (application.agents && application.agents.length > 0) {
      // if (application.agents.length === 1) {
      //   const agent = application.agents[0];
      //   return `${agent.firstName} ${agent.lastName}`;
      // }
      return `${application.agents.length} Agents Assigned`;
    } else if (application.agent) {
      return `${application.agent.firstName} ${application.agent.lastName}`;
    }
    return "Unassigned";
  };

  return (
    <>
      <div className="overflow-x-auto rounded-lg border mx-auto border-gray-500">
        <table className="w-full min-w-full table-auto">
          <thead>
            <tr className="bg-[#005B96] text-left text-base text-white">
              <th className="p-4 font-normal">Applicant Name</th>
              <th className="p-4 font-normal text-center w-[180px] max-w-[220px] whitespace-normal break-words text-wrap">
                Location
              </th>
              <th className="p-4 font-normal text-center min-w-[140px]">DOB</th>
              <th className="p-4 font-normal text-center min-w-[180px]">Visa Type</th>
              <th className="p-4 font-normal text-center min-w-[140px]">Visa Expiry</th>
              <th className="p-4 font-normal text-center min-w-[200px] whitespace-normal break-words">
                Country Of Interest
              </th>
              <th className="p-4 font-normal text-center min-w-[200px]">Process Type</th>
              <th className="p-4 font-normal text-center min-w-[200px]">Agent Name</th>
              <th className="p-4 font-normal text-center">Status</th>
              <th className="p-4 font-normal text-end">Logs</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {filteredApplications.map((application, index) => (
              <tr
                key={index}
                className={index % 2 === 0 ? 'bg-white' : 'bg-slate-50'}
              >
                <td
                  onClick={() => navigate(`/agent/leads/${application._id}`)}
                  className="p-4 cursor-pointer"
                >
                  {application.firstName + ' ' + application.lastName}
                </td>
                <td className="p-4 text-center border-l border-[#005B96] w-[180px] whitespace-normal break-words max-w-[320px]">
                  {application.country}
                </td>
                <td className="p-4 text-center border-l border-[#005B96]">
                  {application?.dob
                    ? moment(application?.dob).format('DD-MM-YYYY')
                    : '-'}
                </td>
                <td className="p-4 text-center border-l border-[#005B96]">{application.visaCategory}</td>
                <td className="p-4 text-center border-l border-[#005B96]">
                  {application?.visaExpiryDate
                    ? moment(application?.visaExpiryDate).format('DD-MM-YYYY')
                    : '-'}
                </td>
                <td className="p-4 text-center border-l border-[#005B96] w-[200px] whitespace-normal break-words">
                  {application.countryOfInterest}
                </td>
                <td className="p-4 text-center border-l border-[#005B96]">{application?.processType?.name || '-'}</td>
                <td className="p-4 text-center border-l border-[#005B96]">
                  <button
                    onClick={() => handleAgentButtonClick(application)}
                    className="inline-flex items-center gap-2 px-3 py-1 border border-gray-300 rounded hover:bg-gray-50 transition-colors"
                  >
                    <span className="text-sm">
                      {getAgentDisplayText(application)}
                    </span>
                    <FaChevronDown size={11}/>
                  </button>
                </td>
                <td className="p-4 text-center border-l border-[#005B96]">
                  {(() => {
                    const raw = String(
                      application?.status || application?.visaStatus || '',
                    );
                    const currentDisplay = toDisplayStatus(raw);

                    return (
                      <select
                        className="border rounded px-3 py-1"
                        value={currentDisplay || ''}
                        onChange={(e) => handleStatusChange(e, application)}
                      >
                        {!currentDisplay && (
                          <option value="" disabled>
                            Select Status
                          </option>
                        )}
                        {DISPLAY_STATUS.map((opt) => (
                          <option
                            key={opt}
                            value={opt}
                            style={{
                              color:
                                opt === 'Cancelled' || opt === 'Withdrawn'
                                  ? '#B74747'
                                  : opt === 'Finalized'
                                    ? '#3A7A15'
                                    : 'inherit',
                            }}
                          >
                            {opt}
                          </option>
                        ))}
                      </select>
                    );
                  })()}
                </td>

                <td className="p-4 text-end border-l border-[#005B96]">
                  <button
                    type="button"
                    className="p-2 rounded hover:bg-gray-100"
                    onClick={() =>
                      navigate(`/agent/application-log/${application._id}`)
                    }
                    aria-label="View logs"
                  >
                    <BsClockHistory className="text-[#1f3b60]" size={18} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {isLoading ? (
          <div className="flex items-center justify-center min-h-[400px]">
            <div className="w-8 h-8 border-4 border-blue-900 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : filteredApplications.length === 0 ? (
          <p className="text-center text-red-500 p-10">
            No applications available
          </p>
        ) : null}
      </div>

      {statusModal.show && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() =>
              setStatusModal({
                show: false,
                applicationId: '',
                newStatus: '',
                reason: '',
                agentAssigned: true,
                visaStatus: '',
              })
            }
          />

          <div className="relative bg-white rounded-lg w-full max-w-md p-6 mx-4">
            <button
              onClick={() =>
                setStatusModal({
                  show: false,
                  applicationId: '',
                  newStatus: '',
                  reason: '',
                  agentAssigned: true,
                  visaStatus: '',
                })
              }
              className="absolute top-2 right-2 text-gray-700 hover:text-gray-900"
            >
              <RxCross2 size={20} />
            </button>

            <h2 className="text-xl font-semibold mb-4">Update Status</h2>

            {!statusModal.agentAssigned && (
              <p className="text-red-600 mb-3">
                ⚠️ Assign this application to an agent first to update status.
              </p>
            )}

            {statusModal.newStatus === 'Finalized' &&
              statusModal.visaStatus !== 'Approved' &&
              statusModal.visaStatus !== 'Rejected' && (
                <p className="text-red-600 mb-3">
                  Visa status should be approved or rejected to finalize this
                  application
                </p>
              )}

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Reason for Status Update
                </label>
                <textarea
                  value={statusModal.reason}
                  onChange={(e) =>
                    setStatusModal({ ...statusModal, reason: e.target.value })
                  }
                  className="w-full h-32 p-3 border border-gray-300 rounded-md resize-none"
                  placeholder="Enter reason..."
                  disabled={!statusModal.agentAssigned}
                />
              </div>
            </div>

            <div className="flex justify-end mt-2">
              {isSubmittingStatus ? (
                <button
                  onClick={() =>
                    setStatusModal({
                      show: false,
                      applicationId: '',
                      newStatus: '',
                      reason: '',
                      agentAssigned: true,
                      visaStatus: '',
                    })
                  }
                  className="h-[40px] w-[129.18px] bg-[#005887c2] text-white rounded-full flex items-center justify-center"
                >
                  <span className="loader"></span>
                </button>
              ) : (
                <button
                  onClick={confirmStatusUpdate}
                  disabled={
                    !statusModal.agentAssigned ||
                    !statusModal.reason ||
                    !statusModal.reason.trim() ||
                    (statusModal.newStatus === 'Finalized' &&
                      statusModal.visaStatus !== 'Approved' &&
                      statusModal.visaStatus !== 'Rejected')
                  }
                  className={`px-8 py-2 rounded-full transition-colors ${
                    !statusModal.agentAssigned ||
                    !statusModal.reason ||
                    !statusModal.reason.trim() ||
                    (statusModal.newStatus === 'Finalized' &&
                      statusModal.visaStatus !== 'Approved' &&
                      statusModal.visaStatus !== 'Rejected')
                      ? 'bg-gray-300 text-gray-600 cursor-not-allowed'
                      : 'bg-[#005787] text-white hover:bg-[#005887c2]'
                  }`}
                >
                  Update
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      <MultiAgentAssignmentModal
        isOpen={multiAgentModal.show}
        agents={filteredAgents}
        selectedAgents={multiAgentModal.selectedAgents}
        currentAgents={multiAgentModal.currentAgents}
        isSubmitting={isSubmittingAgents}
        onClose={closeMultiAgentModal}
        reason={multiAgentModal.reason}
        requireReason={true}
        onToggleAgent={handleAgentCheckboxChange}
        onSubmit={handleMultiAgentSubmit}
        onReasonChange={(value)=>setMultiAgentModal((prev) => ({ ...prev, reason: value }))
}
      />
    </>
  );
};

export default LeadsTable;
